import type { Gender, UserCreate, UserDocumentCreate, UserPublic } from '../type/user';
import type { Member, MemberFormData } from '@/types';

const toDisplayCase = (value?: string | null) => {
  if (!value) return undefined;
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

const toNameCase = (value?: string | null) => {
  const trimmed = value?.trim();
  if (!trimmed) return '';
  return trimmed.replace(/\S+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
};

export const UserAPIResponseMapper = (user: UserPublic): Member => {
  const contact = user.contacts?.[0];
  const nominee = user.nominees?.[0];
  const documents = user.documents ?? [];

  return {
    id: String(user.id),
    name: user.full_name ?? '',
    fatherOrHusbandName: user.father_husband_name ?? undefined,
    gender: toDisplayCase(user.gender) as Member['gender'],
    dob: user.date_of_birth ?? undefined,
    educationQualification: user.education_qualification ?? undefined,
    occupation: user.occupation ?? undefined,
    phone: contact?.phone ?? '',
    email: user.email || undefined,
    photo: user.profile_photo?.url,
    profileImage: user.profile_photo?.url,
    joinedDate: user.joined_date ?? '',
    endDate: user.end_date ?? '',
    nomineeName: nominee?.nominee_name,
    nomineeRelationship: nominee?.nominee_relationship,
    proof1: documents[0]?.document_name,
    proof1Url: documents[0]?.file_url,
    proof2: documents[1]?.document_name,
    proof2Url: documents[1]?.file_url,
    address: contact?.address ?? undefined,
    lifeStatus: (user.life_status?.toLowerCase() as Member['lifeStatus']) ?? 'alive',
  };
};

export const UserAPIRequestMapper = (form: MemberFormData): UserCreate => {
  const documents: UserDocumentCreate[] = [
    { document_name: form.proof1, file_url: form.proof1Url },
    { document_name: form.proof2, file_url: form.proof2Url },
  ]
    .filter((doc) => doc.file_url)
    .map((doc) => ({ document_name: doc.document_name || 'Document', file_url: doc.file_url! }));

  return {
    email:form.email?.trim() || null,
    full_name: toNameCase(form.name),
    father_husband_name: form.fatherOrHusbandName?.trim() ? toNameCase(form.fatherOrHusbandName) : null,
    gender: toDisplayCase(form.gender) as Gender,
    date_of_birth: form.dob,
    age: form.age ?? null,
    education_qualification: form.educationQualification?.trim() || null,
    occupation: form.occupation?.trim() || null,
    joined_date: form.joinedDate,
    end_date: form.endDate || null,
    // undefined = keep current photo, '' = remove photo, URL = new photo.
    profile_photo: form.photo,
    contacts: [
      {
        phone: form.phone,
        email:form.email?.trim() || null,
        address: form.address?.trim() || null,
        is_primary: true,
      },
    ],
    nominees: [
      {
        nominee_name: toNameCase(form.nomineeName),
        nominee_relationship: form.nomineeRelationship || '',
      },
    ],
    documents,
  };
};
